import * as listService from './service';

/**
 * CRM List Management Actions
 * 
 * Domain Logic:
 * - Manages saved list views for different CRM entities (contacts, accounts, opportunities)
 * - Handles default list selection per entity type for user preferences
 * - Supports list CRUD operations with entity-specific data
 * - Provides error handling for backend list operations
 * - Maintains list state synchronization across entity types
 */

/**
 * Fetches all default lists for the user across all entity types
 * Used to initialize user's preferred list views
 */
export const fetchDefaultLists = () => async (dispatch) => {
  dispatch({ type: 'LISTS_LOADING' });
  try {
    const data = await listService.getDefaultLists();
    dispatch({ type: 'DEFAULT_LISTS_LOADED', data: data || [] });
  } catch (e) {
    dispatch({ type: 'LISTS_ERROR', error: e.message });
  }
};

/**
 * Fetches all lists for a specific CRM entity type
 * Loads saved views for contacts, accounts, opportunities, etc.
 */
export const fetchListsForEntity = (vendorEntityType) => async (dispatch) => {
  dispatch({ type: 'LISTS_LOADING' });
  try {
    const data = await listService.getLists(vendorEntityType);
    dispatch({ type: 'LISTS_LOADED', entityType: vendorEntityType, data: data || [] });
  } catch (e) {
    dispatch({ type: 'LISTS_ERROR', error: e.message });
  }
};

/**
 * Sets the default list for a specific entity type
 * Updates user preference for which list view to show by default
 */
export const setDefaultList = (entityType, listId) => async (dispatch) => {
  try {
    await listService.setDefaultList(entityType, listId);
    dispatch({ type: 'SET_DEFAULT_ID', entityType, listId });
  } catch (e) {
    dispatch({ type: 'LISTS_ERROR', error: e.message });
  }
};

/**
 * Updates an existing list (rename, column configuration, etc.)
 * Handles list modifications while preserving entity associations
 */
export const updateList = (entityType, list) => async (dispatch) => {
  try {
    const result = await listService.updateList(list.id, list);
    if (result?.status === 'error') {
      dispatch({ type: 'LISTS_ERROR', error: result.message });
      return result;
    }
    dispatch({ type: 'UPDATE_LIST', entityType, list });
    return result;
  } catch (e) {
    const backendMsg = e.response?.data?.message || e.message;
    dispatch({ type: 'LISTS_ERROR', error: backendMsg });
    return { status: 'error', message: backendMsg };
  }
};

/**
 * Creates a new list view for a CRM entity
 * Initializes list with entity type and default column configuration
 */
export const createList = (list) => async (dispatch) => {
  try {
    const result = await listService.createList(list);
    if (result?.status === 'error') {
      dispatch({ type: 'LISTS_ERROR', error: result.message });
      return result;
    }
    dispatch(fetchListsForEntity(list.entity_type));
    return result;
  } catch (e) {
    const backendMsg = e.response?.data?.message || e.message;
    dispatch({ type: 'LISTS_ERROR', error: backendMsg });
    return { status: 'error', message: backendMsg };
  }
};

/**
 * Deletes a list view for a specific entity type
 * Removes saved view and refreshes entity's list collection
 */
export const deleteList = (entityType, id) => async (dispatch) => {
  try {
    const result = await listService.deleteList(id);
    dispatch(fetchListsForEntity(entityType));
    return result;
  } catch (e) {
    const backendMsg = e.response?.data?.message || e.message;
    dispatch({ type: 'LISTS_ERROR', error: backendMsg });
    return { status: 'error', message: backendMsg };
  }
};

/**
 * Clears any error state from list operations
 * Resets error handling for subsequent list operations
 */
export const clearListsError = () => (dispatch) => {
  dispatch({ type: 'LISTS_ERROR', error: null });
}; 